import { combineReducers } from 'redux';

import { call, put, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { TaxViewVO } from 'generated/bslEbill/taxView/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';

import { ApiClientRequest } from 'models/ApiClientRequest';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';

/* STATE */
export interface TaxViewState {
  data: TaxViewVO;
  loadingStatus: LoadingStatus;
}

/* ACTION TYPES */
export enum TaxViewActionTypes {
  GetData = '@@TaxView/GET_DATA',
  SetLoading = '@@TaxView/SET_LOADING',
}

/* ACTIONS */
export const getTaxViewDataActions = createApiActionCreators(TaxViewActionTypes.GetData);
export const setLoadingAction = createActionCreator(TaxViewActionTypes.SetLoading);

/* REDUCERS */
const initialState: TaxViewState = {
  data: {},
  loadingStatus: LoadingStatus.loading,
};

const data = createReducer(initialState.data, {
  [TaxViewActionTypes.GetData]: {
    [RequestActionTypes.SUCCESS]: (state: TaxViewVO, payload: TaxViewVO) => payload,
    [RequestActionTypes.FAILURE]: () => {
      undefined;
    },
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [TaxViewActionTypes.GetData]: [RequestActionTypes.REQUEST, RequestActionTypes.SUCCESS, RequestActionTypes.FAILURE],
  },
  {
    [TaxViewActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

export default combineReducers<TaxViewState>({
  data,
  loadingStatus,
});

/* SELECTORS*/
const selectTaxView = (state: AppState) => state.taxView;

export const selectTaxViewData = (state: AppState): TaxViewVO => selectTaxView(state).data;
export const selectTaxViewLoading = (state: AppState): LoadingStatus => selectTaxView(state).loadingStatus;

/* SAGAS */

function* getTaxViewData({ payload: invoiceNumber }: AppAction<string>) {
  yield put(setLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(api.getData, yield getApiUrl(environment.urlConfig.billingManagement), invoiceNumber),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getTaxViewDataActions.success(resp.data));
  } else {
    yield put(getTaxViewDataActions.failure());
  }
}

/* EXPORT */
export function* taxViewSaga() {
  yield takeLatest(createActionType(TaxViewActionTypes.GetData, RequestActionTypes.REQUEST), getTaxViewData);
}
